import React from 'react';

import cn from 'classnames';

import { FemtasyIcon } from 'components/IconFemtasy/iconsType';
import Message from 'components/Message';
import NestedLink from 'components/NestedLink';
import Typography from 'components/Typography';

import { IntlMsgShape } from 'helpers/consts';
import triggerAnalyticsEvent from 'helpers/trigger-analytics-event';

import { Color } from 'types/colors';

import './style.scss';

export type ButtonVariant = 'primary' | 'secondary' | 'white';

export interface IProps {
  'aria-label'?: string;
  analyticsEvent?: string[];
  className?: string;
  'data-testid'?: string;
  disabled?: boolean;
  icon?: {
    component: React.ReactNode | FemtasyIcon;
    alignment?: 'left' | 'right';
  };
  linkTo?: string;
  loading?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  role?: string;
  small?: boolean;
  target?: string;
  title: IntlMsgShape | string;
  type?: 'button' | 'submit' | 'reset';
  variant?: ButtonVariant;
}

const Button = ({
  'aria-label': ariaLabel,
  analyticsEvent,
  className,
  'data-testid': dataTestId,
  disabled,
  icon,
  linkTo,
  loading,
  onClick,
  role,
  small,
  target,
  title,
  type,
  variant = 'primary',
}: IProps) => {
  const buttonClassNames = cn(
    'Button',
    'Button--' + variant,
    {
      'Button--loading': loading,
      'Button--disabled': disabled,
      'Button--small': small,
      'Button--icon-right': icon?.alignment === 'right',
    },
    className,
  );

  const handleClick = React.useCallback(
    (e) => {
      analyticsEvent && triggerAnalyticsEvent({ event: analyticsEvent });
      onClick && onClick(e);
    },
    [analyticsEvent, onClick],
  );

  const textColor = (variant: ButtonVariant): Color => {
    switch (variant) {
      case 'secondary':
      case 'white':
        return 'dark-body';
      default:
        return 'white';
    }
  };

  const content = (
    <div className="Button__inner">
      {icon?.component}
      <Typography
        variant={small ? 'small-thick' : 'button'}
        color={textColor(variant)}
      >
        <Message message={title} />
      </Typography>
    </div>
  );

  if (linkTo && !disabled) {
    return (
      <NestedLink
        className={buttonClassNames}
        data-testid={dataTestId}
        onClick={handleClick}
        to={linkTo}
        role={role}
        aria-label={ariaLabel}
        target={target}
      >
        {content}
      </NestedLink>
    );
  } else {
    return (
      <button
        className={buttonClassNames}
        data-testid={dataTestId}
        onClick={handleClick}
        disabled={disabled || loading}
        role={role}
        aria-label={ariaLabel}
        type={type}
      >
        {content}
      </button>
    );
  }
};

export default Button;
